import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { HandleErrorService } from './handle-error.service';

/**
 * Interceptor que aplica lógica de autenticación para microservicios:
 * - Inyecta Authorization Bearer token automáticamente
 * - Implementa lógica de refresh token ante errores 401
 * - Respeta Authorization explícito (incluyendo 'none')
 */
@Injectable()
export class MicroservicesHttpAuthInterceptor implements HttpInterceptor {

  constructor(
    private readonly handleErrorS: HandleErrorService
  ) { }

  public intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

    if (!this.isApiRequest(request)) {
      return next.handle(request);
    }

    const originalAuthHeader = request.headers.get('Authorization');

    // * Si el request original trae Authorization (incluso 'none'), no aplicar lógica de refresh
    if (originalAuthHeader) {
      if (originalAuthHeader === 'none') {
        request = request.clone({ headers: request.headers.delete('Authorization') });
      }
      return next.handle(request); // * Ya trae un token, se respeta
    }

    // ? Manejo del refresh solo si no hay Authorization explícito
    return this.addTokenToRequest(request).pipe(
      switchMap(authRequest => next.handle(authRequest)
        .pipe(
          catchError(error => {
            if (error instanceof HttpErrorResponse) {
              return this.handleErrorS.handleHttpStatusError(error, (newToken) => {
                const retryReq = request.clone({
                  headers: request.headers.set('Authorization', `Bearer ${newToken}`)
                });
                return next.handle(retryReq);
              });
            }
            throw error;
          })
        )
      )
    );
  }

  private isApiRequest(request: HttpRequest<any>): boolean {
    const apiEndpoints = [environment.urlApiV2];
    return apiEndpoints.some(endpoint => request.url.startsWith(endpoint));
  }

  private addTokenToRequest = (request: HttpRequest<any>): Observable<HttpRequest<any>> =>
    this.handleErrorS.getToken$().pipe(
      switchMap(token => {
        if (!token) throw new Error(`Token no disponible: marque la solicitud con 'Authorization: none' si no requiere autenticación. (${request.url})`);

        // Token inválido → Forzar refresh
        if (this.handleErrorS.validarJWT(token)) {
          return of(request.clone({ headers: request.headers.set('Authorization', `Bearer ${token}`) }));
        }

        return this.handleErrorS.handle401Error((newToken) =>
          of(request.clone({ headers: request.headers.set('Authorization', `Bearer ${newToken}`) }))
        );
      })
    );

}